/**
 * CollisionWorld の AABB / OBB を、カメラ周辺の解析 SDF テクスチャへ焼く。
 * 解析枠に載ったプレーン・ブレースは焼かない。枠から溢れた分とブロックはここに焼く。
 * 静的コライダはキャッシュし、原点スナップ時は重なりをずらして新規スラブだけ焼き直す。
 * アバターなど extra は静的結果のコピーへ毎フレーム足す。
 */

import * as THREE from 'three';
import type { GraphicsQuality } from './graphics-quality';
import type { Aabb, CollisionWorld } from '../shared/physics';
import {
  advanceVoxelSdf,
  buildVoxelSdf,
  stampColliders,
  stampLeftoverPrimitives,
  volumeBounds,
  volumeFromCenter,
  volumesEqual,
  type VoxelStampLeftover,
  type VoxelVolume,
} from '../shared/voxel-sdf';

export class VoxelSdfGpu {
  private static readonly NO_SKIP: ReadonlySet<string> = new Set();
  texture: THREE.Data3DTexture;
  readonly origin = new THREE.Vector3();
  extent: number;
  resolution: number;
  private volume: VoxelVolume;
  private collisionRevision = -1;
  private skipKey = '';
  private extraBoxes: Aabb[] = [];
  private staticSdf: Float32Array;

  constructor(quality: GraphicsQuality) {
    this.extent = quality.voxelExtent;
    this.resolution = quality.voxelResolution;
    this.volume = volumeFromCenter({ x: 0, y: 0, z: 0 }, this.extent, this.resolution);
    this.staticSdf = new Float32Array(this.resolution ** 3);
    this.staticSdf.fill(this.extent);
    this.texture = this.makeTexture(new Float32Array(this.staticSdf));
  }

  get currentVolume(): VoxelVolume {
    return this.volume;
  }

  applyQuality(quality: GraphicsQuality): void {
    if (this.resolution === quality.voxelResolution && this.extent === quality.voxelExtent) return;
    this.extent = quality.voxelExtent;
    this.resolution = quality.voxelResolution;
    this.volume = volumeFromCenter({ x: 0, y: 0, z: 0 }, this.extent, this.resolution);
    this.staticSdf = new Float32Array(this.resolution ** 3);
    this.staticSdf.fill(this.extent);
    this.texture.dispose();
    this.texture = this.makeTexture(new Float32Array(this.staticSdf));
    this.collisionRevision = -1;
    this.skipKey = '';
    this.extraBoxes = [];
  }

  rebuild(
    collision: CollisionWorld,
    extraBoxes: readonly Aabb[],
    center: THREE.Vector3,
    skipIds: ReadonlySet<string> = VoxelSdfGpu.NO_SKIP,
    leftover: VoxelStampLeftover = {}
  ): void {
    const next = volumeFromCenter(center, this.extent, this.resolution);
    const originChanged = !volumesEqual(this.volume, next);
    const extrasChanged = !boxesEqual(this.extraBoxes, extraBoxes);
    const collisionChanged = collision.revision !== this.collisionRevision;
    const skipKey = skipSignature(skipIds);
    const skipChanged = skipKey !== this.skipKey;
    if (!originChanged && !collisionChanged && !skipChanged && !extrasChanged) {
      return;
    }

    if (collisionChanged || skipChanged || originChanged || this.collisionRevision < 0) {
      const colliders = collision.query(volumeBounds(next), [], skipIds);
      const leftoverCount =
        (leftover.planes?.length ?? 0) +
        (leftover.triangles?.length ?? 0) +
        (leftover.braces?.length ?? 0);
      const shifted =
        leftoverCount === 0 &&
        !collisionChanged &&
        !skipChanged &&
        originChanged &&
        this.collisionRevision >= 0
          ? advanceVoxelSdf(this.staticSdf, this.volume, next, colliders)
          : null;
      this.staticSdf = shifted ?? buildVoxelSdf(next, colliders, []);
      stampLeftoverPrimitives(this.staticSdf, next, leftover);
    }

    this.volume = next;
    this.origin.set(next.origin.x, next.origin.y, next.origin.z);
    this.collisionRevision = collision.revision;
    this.skipKey = skipKey;
    this.extraBoxes = extraBoxes.map((box) => ({
      min: { ...box.min },
      max: { ...box.max },
    }));

    const image = this.texture.image as unknown as { data: Float32Array };
    if (image.data.length !== this.staticSdf.length) {
      image.data = new Float32Array(this.staticSdf.length);
    }
    image.data.set(this.staticSdf);
    if (extraBoxes.length > 0) stampColliders(image.data, next, [], extraBoxes);
    this.texture.needsUpdate = true;
  }

  dispose(): void {
    this.texture.dispose();
  }

  private makeTexture(data: Float32Array): THREE.Data3DTexture {
    const texture = new THREE.Data3DTexture(
      data as unknown as BufferSource,
      this.resolution,
      this.resolution,
      this.resolution
    );
    texture.format = THREE.RedFormat;
    texture.type = THREE.FloatType;
    texture.internalFormat = 'R32F';
    texture.colorSpace = THREE.NoColorSpace;
    // R32F は線形フィルタが必須ではない。LINEAR だと incomplete になり 0 ばかり読まれる。
    texture.minFilter = THREE.NearestFilter;
    texture.magFilter = THREE.NearestFilter;
    texture.generateMipmaps = false;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.wrapR = THREE.ClampToEdgeWrapping;
    texture.unpackAlignment = 1;
    texture.needsUpdate = true;
    return texture;
  }
}

const BOX_CHANGE_EPSILON = 0.02;

function boxesEqual(a: readonly Aabb[], b: readonly Aabb[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const aa = a[i];
    const bb = b[i];
    if (
      Math.abs(aa.min.x - bb.min.x) > BOX_CHANGE_EPSILON ||
      Math.abs(aa.min.y - bb.min.y) > BOX_CHANGE_EPSILON ||
      Math.abs(aa.min.z - bb.min.z) > BOX_CHANGE_EPSILON ||
      Math.abs(aa.max.x - bb.max.x) > BOX_CHANGE_EPSILON ||
      Math.abs(aa.max.y - bb.max.y) > BOX_CHANGE_EPSILON ||
      Math.abs(aa.max.z - bb.max.z) > BOX_CHANGE_EPSILON
    ) {
      return false;
    }
  }
  return true;
}

function skipSignature(ids: ReadonlySet<string>): string {
  if (ids.size === 0) return '';
  return [...ids].sort().join('\0');
}
